<template>
  <div>

    <h4>Period</h4>
    <form
      @submit.prevent="loadActions"
      class="mb-4"
    >
      <table class="period-table">
        <tr>
          <td>Begin</td>
          <td>
            <date-input v-model="beginDateStr"/>
          </td>
          <td>
            <time-input v-model="beginTimeStr"/>
          </td>
        </tr>
        <tr>
          <td>End</td>
          <td>
            <date-input v-model="endDateStr"/>
          </td>
          <td>
            <time-input v-model="endTimeStr"/>
          </td>
        </tr>
      </table>
      <b-button type="submit" variant="primary">Reload</b-button>
    </form>

    <template
      v-if="loadState === LOAD_STATE_LOADED_NORMAL"
    >

      <p v-if="message !== null">
        {{message}}
      </p>

      <table v-if="actions !== null && actions.length > 0" class="table text-nowrap">
        <thead class="thead-dark">
          <tr>
            <th scope="col">Id</th>
            <th scope="col">Time</th>
            <th scope="col">IP</th>
            <th scope="col">Type</th>
            <th scope="col">User</th>
            <th scope="col">Info</th>
          </tr>
        </thead>
        <tr
          v-for="action in actions"
          :key="action.id"
        >
          <td>{{action.id}}</td>
          <td>{{action.ctime | getMomentStr}}</td>
          <td>{{action.userIP | getOptionalStringDefaultStr('unknown')}}</td>
          <td>{{action.type | getEnumInsiderUserActionTypeName}}</td>
          <td>
            <user-link
              :user="getUser(action.userId)"
            />
          </td>
          <td>
            <user-action-info
              :action="action"
            />
          </td>
        </tr>
      </table>

    </template>

    <loading-indicator
      v-if="isLoading"
    />

  </div>
</template>

<script>
import { HTTP } from '../http-common';
import notifier from '../notifier';
import LoadingIndicator from './LoadingIndicator.vue';
import UserActionInfo from './UserActionInfo.vue';
import UserLink from './UserLink.vue';
import DateInput from './DateInput.vue';
import TimeInput from './TimeInput.vue';
import { getEnumInsiderUserActionTypeName } from '../autogen/bundle';

import { convertUserArrayToMap               } from '../model/user';
import { convertMomentToDataStr              } from '../util';
import { convertMomentToTimeStr              } from '../util';
import { convertMomentToSec                  } from '../util';
import { convertDataStrAndTimeStrToLocalData } from '../util';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'PlayerUsersActions',

  components: {
    LoadingIndicator,
    UserActionInfo,
    UserLink,
    DateInput,
    TimeInput,
  },

  props: {
    playerId: {
      type: Number,
      required: true,
    }
  },

  data() {
    const now = new Date();

    const DEFAULT_DURATION_HOURS = 24;
    const begin = new Date(now.getTime());
    begin.setHours(begin.getHours() - DEFAULT_DURATION_HOURS);
    const beginDateStr = convertMomentToDataStr(begin);
    const beginTimeStr = convertMomentToTimeStr(begin);

    const endDateStr = convertMomentToDataStr(now);
    const endTimeStr = convertMomentToTimeStr(now);

    return {
      LOAD_STATE_LOADED_NORMAL,

      MONDAY: 1,

      beginDateStr,
      beginTimeStr,
      endDateStr,
      endTimeStr,

      loadState: LOAD_STATE_INIT,
      actions: null,
      userMap: null,
    };
  },

  computed: {
    beginMoment() {
      return convertDataStrAndTimeStrToLocalData(this.beginDateStr, this.beginTimeStr);
    },

    endMoment() {
      return convertDataStrAndTimeStrToLocalData(this.endDateStr, this.endTimeStr);
    },

    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    message() {
      if(this.actions !== null && this.actions.length === 0) {
        return 'No actions.';
      }
      return null;
    },
  },

  created() {
    this.loadActions();
  },

  methods: {
    loadActions() {
      notifier.deleteSenderNotifications(this);

      const beginM = this.beginMoment;
      const endM = this.endMoment;
      if(beginM === null || endM === null) {
        notifier.showError(
          'Period is incorrect.',
          {sender: this},
        );
        return;
      }

      const beginMSec  = convertMomentToSec(beginM);
      const endMSec    = convertMomentToSec(endM);

      if(endMSec < beginMSec) {
        notifier.showError(
          'Period is incorrect. Begin must be before end.',
          {sender: this},
        );
        return;
      }

      this.actions = null;
      this.loadState = LOAD_STATE_LOADING;

      const url = `/player/${this.playerId}/users_actions`;
      const params = {
        begin: beginMSec,
        end: endMSec,
      };

      HTTP.get(
        url,
        {
          params: params,
        }
      )
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        const actions = data.actions;
        actions.sort((a, b) => b.id - a.id);
        this.actions = actions;

        if('users' in data) {
          this.userMap = convertUserArrayToMap(data.users);
        }

        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(e => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(e, {sender: this});
      });
    },

    getUser(userId) {
      const user = this.userMap.get(userId);
      if(user === undefined) {
        return {
          id: userId,
        };
      }
      return user;
    },
  },

  filters: {
    getEnumInsiderUserActionTypeName,
  },
};
</script>

<style scoped>
  .period-table > tr > td {
    padding-right: 0.4em;
    padding-bottom: 0.4em;
  }
</style>